/**
 * The pieces the six page templates share — the designer's brief part 1 §1 and §5, part 2 §2–§5,
 * `docs/adr/9032`. Each is data-agnostic: it draws strings and nodes the caller has already
 * resolved, and it computes no figure.
 *
 *   `TemplateSentence`   the conclusion, Antonio uppercase, a catalogue key and its arguments
 *   `KpiRow`             at most four figures under a 2px ink rule, mono 600
 *   `ConstantsBand`      «Etichetta **valore**» pairs said once — THE band, `../constants-band`
 *   `ProvenanceLine`     «PROVENIENZA» and the source, date and run in mono
 *   `TemplateStatus`     the page's ONE state strip (ADR 9033), under the sentence
 *   `InAppAnchor`        a real `href` that stays in the app on a plain click
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert } from 'lucide-react';

import type { ICUValues } from '../../primitives';
import { AboutPopover, FOCUS_RING, KpiLinkAbove, KpiLinkAnchor, useKpiLink } from '../../primitives';
import { ArrivingTag } from '../arriving';
import { PageStatusProvider, PageStatusStrip, useInPageStatus, type PageStatusSentence } from '../page-status';
import { navigateInApp } from './url';

/** A sentence from the catalogue: a key and its ICU arguments. The rule that picks it is the caller's. */
export type TemplateSentence = PageStatusSentence;

/** The most figures a template's KPI row holds (brief part 1 §5: «al massimo 4»). */
export const TEMPLATE_KPI_MAX = 4;

// ── the sentence ─────────────────────────────────────────────────────────────

/**
 * The conclusion, with real numbers (brief part 1 §5): Antonio uppercase, 26px. `live` announces
 * a sentence that changes while the planner types — the calculator's answer.
 */
export function SentenceLine({ sentence, live = false, size = 26 }: {
  readonly sentence: TemplateSentence;
  readonly live?: boolean;
  /** 26 on a page; 28 is the drill's title and is drawn by `DrillHeader`. */
  readonly size?: 22 | 26;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <p
      data-template-sentence=""
      data-tour="headline"
      {...(live ? { 'aria-live': 'polite' as const, 'aria-atomic': true } : {})}
      className={`font-display text-ink-primary max-w-[60rem] text-pretty font-bold uppercase leading-[1.1] ${size === 26 ? 'text-[26px]' : 'text-[22px]'}`}
    >
      {t(sentence.key, sentence.values ?? {})}
    </p>
  );
}

// ── the KPI row ──────────────────────────────────────────────────────────────

export interface TemplateKpi {
  readonly id: string;
  readonly labelKey: string;
  readonly labelValues?: ICUValues;
  /** The figure, already formatted in the planner's locale. `null`: «—», a figure that is absent. */
  readonly value: string | null;
  /** The line under the figure, already a sentence — «in 3 gruppi», «dal 27/09». */
  readonly context?: string | null;
  /**
   * The platform does not publish this figure yet (DEC-96 §4): «—» in `mark-neutral` with the
   * «In arrivo» tag under it, whatever `value` holds. Never an estimate.
   */
  readonly arriving?: boolean;
  /**
   * The figure is past a threshold the caller owns — the oldest approval over two days. Drawn in
   * `status-critical-strong` with an icon and this sentence for assistive technology, never the
   * colour alone (§12.6).
   */
  readonly alert?: TemplateSentence;
  /**
   * What the figure really is — definition, grain, source, as-of and limits (DEC-111, ADR 9047):
   * an (i) after the label. `descriptionValues` fills it with what the page read.
   */
  readonly descriptionKey?: string;
  readonly descriptionValues?: ICUValues;
  /**
   * The list behind the figure (`docs/adr/9069`): the whole card becomes a link to it, built with
   * the screen's own route helpers; the list must sum to this figure. The (i) stays outside the
   * link. Not drawn for an absent or «In arrivo» figure. Without it the card is what it always was.
   */
  readonly href?: string;
}

/**
 * At most four figures under a 2px ink rule (brief part 1 §5). More than four is a caller's
 * mistake, not a layout to wrap, so it throws — as an event line on an undeclared axis does
 * (ADR 0011).
 */
export function KpiRow({ kpis, size = 26 }: {
  readonly kpis: readonly TemplateKpi[];
  /** The figure's size: 26 on a page and a calculator, 22 in a «Perché» explanation. */
  readonly size?: 22 | 26;
}): React.JSX.Element | null {
  if (kpis.length > TEMPLATE_KPI_MAX) {
    throw new Error(`a template's KPI row holds at most ${TEMPLATE_KPI_MAX} figures; got ${kpis.length}`);
  }
  if (kpis.length === 0) return null;
  return (
    <dl data-template-kpis="" data-layout-block="" data-tour="kpi-row" className="border-ink-primary grid grid-cols-2 border-t-2 lg:grid-cols-4">
      {kpis.map((kpi) => <KpiFigure key={kpi.id} kpi={kpi} size={size} />)}
    </dl>
  );
}

function KpiFigure({ kpi, size }: { readonly kpi: TemplateKpi; readonly size: 22 | 26 }): React.JSX.Element {
  const { t } = useTranslation();
  const figureSize = size === 26 ? 'text-[26px] leading-[32px]' : 'text-[22px] leading-[28px]';
  const absent = kpi.arriving === true || kpi.value === null;
  const link = useKpiLink(absent ? undefined : kpi.href);
  return (
    <div data-kpi={kpi.id} className={`flex min-w-0 flex-col gap-[2px] py-3 pe-3${link.rootClass === '' ? '' : ` ${link.rootClass}`}`}>
      <dt className="text-ink-secondary flex items-center gap-1 text-[11px] uppercase leading-4 tracking-[0.06em]">
        <span {...link.labelProps} className="min-w-0">{t(kpi.labelKey, kpi.labelValues ?? {})}</span>
        {kpi.descriptionKey !== undefined && (
          <KpiLinkAbove link={link}>
            <AboutPopover
              aboutKeys={[kpi.descriptionKey]}
              title={t(kpi.labelKey, kpi.labelValues ?? {})}
              size="sm"
              {...(kpi.descriptionValues === undefined ? {} : { values: kpi.descriptionValues })}
            />
          </KpiLinkAbove>
        )}
      </dt>
      <dd className="flex flex-col gap-1">
        {absent ? (
          <span className={`font-numeric text-ink-secondary font-semibold ${figureSize}`}>
            {t(kpi.arriving === true ? 'pageStatus.arriving.value' : 'template.kpi.absent')}
          </span>
        ) : (
          <KpiLinkAnchor link={link} className="flex flex-col gap-1">
            {kpi.alert !== undefined ? (
              <span {...link.valueProps} className={`font-numeric text-status-critical-strong inline-flex items-center gap-1 font-semibold tabular-nums ${figureSize}`}>
                <CircleAlert aria-hidden className="size-[16px] shrink-0" />
                <span>{kpi.value}</span>
                <span className="sr-only">{t(kpi.alert.key, kpi.alert.values ?? {})}</span>
              </span>
            ) : (
              <span {...link.valueProps} className={`font-numeric text-ink-primary font-semibold tabular-nums ${figureSize}`}>{kpi.value}</span>
            )}
            {kpi.context !== undefined && kpi.context !== null ? (
              // Wrapped, not cut (usability audit D9): the context says what the figure is OF.
              <span className="text-ink-secondary text-[11px] leading-4 [overflow-wrap:anywhere]" data-kpi-context="">{kpi.context}</span>
            ) : null}
          </KpiLinkAnchor>
        )}
        {/* A present figure carries its context inside the (possible) link above; an absent one here. */}
        {kpi.arriving === true ? (
          <span><ArrivingTag /></span>
        ) : absent && kpi.context !== undefined && kpi.context !== null ? (
          // Wrapped, not cut (usability audit D9): the context says what the figure is OF.
          <span className="text-ink-secondary text-[11px] leading-4 [overflow-wrap:anywhere]" data-kpi-context="">{kpi.context}</span>
        ) : null}
      </dd>
    </div>
  );
}

// ── the constants band ───────────────────────────────────────────────────────

/**
 * The templates draw THE constants band, the one a table draws above its header
 * (`../constants-band`), boxed on its own: «Etichetta **valore**» pairs on `surface-constants`,
 * 1px `separator` rules between them, the label in `ink-label`, the value at 600.
 */
export { ConstantsBand, type ConstantPair } from '../constants-band';

// ── the provenance line ──────────────────────────────────────────────────────

/**
 * «PROVENIENZA» 10px 700, then what a planner reads — the run in words, the source's name, a date —
 * and, collapsed behind «Dettagli tecnici», the paths, codes and identifiers support reads
 * (audit A2, 2026-10-10; `./provenance-line.tsx`). The parts are the caller's; nothing is filled in.
 */
export { ProvenanceLine } from './provenance-line';

// ── the page's one strip ─────────────────────────────────────────────────────

/**
 * The template's head (its sentence), then the page's ONE state strip (ADR 9033), then its body.
 * A page already inside a `PageStatusProvider` has its strip drawn elsewhere, and a second one
 * would be a second statement of the same state: then this mounts neither.
 */
export function TemplateStatus({ head, children }: {
  readonly head?: ReactNode;
  readonly children: ReactNode;
}): React.JSX.Element {
  const inPage = useInPageStatus();
  if (inPage) return <>{head}{children}</>;
  return (
    <PageStatusProvider>
      {head}
      <PageStatusStrip />
      {children}
    </PageStatusProvider>
  );
}

// ── a link that stays in the app ─────────────────────────────────────────────

/**
 * A real `href`, so a middle click and "copy link" give the shareable URL, and a plain click
 * stays in the app with a history entry.
 */
export function InAppAnchor({ href, className, children, label, ...rest }: {
  readonly href: string;
  readonly className: string;
  readonly children: ReactNode;
  /** An accessible name, when the visible text is not one. */
  readonly label?: string;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'className' | 'children' | 'onClick'>): React.JSX.Element {
  return (
    <a
      {...rest}
      href={href}
      className={`${className} ${FOCUS_RING}`}
      {...(label === undefined ? {} : { 'aria-label': label })}
      onClick={(event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigateInApp(href);
      }}
    >
      {children}
    </a>
  );
}
